import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import {
  IconBarrierBlock,
  IconCalendarMonth,
  IconClockPlus,
  IconCurrencyDollar,
  IconExclamationCircle,
  IconUserCircle,
} from '@tabler/icons-react';
import React from 'react';
import { ActivityJobDto, PostDto } from 'src/api/FiretrolApiClient';
import { useActivityFeed } from 'src/api/queries/posts/useActivityFeed';
import { useAuth } from 'src/hooks/useAuth';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { LinkButton } from 'src/design/atoms/LinkButton';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { ActivityFeed } from 'src/routes/CTC/components/ActivityFeed';
import { JobTable } from './JobTable';

export const ByJobActivity: React.FC = () => {
  const { data, isLoading: loading } = useActivityFeed();
  const jobs = React.useMemo(() => data ?? [], [data]);
  const [selectedJob, setSelectedJob] = React.useState<{
    id: number;
    jobNumber: string;
  } | null>(null);
  const [jobName, setJobName] = React.useState<string | null>(null);
  const [filters, setFilters] = React.useState({
    constraint: false,
    ctc: false,
    critical: false,
    cost: false,
    unresolved: false,
    assignedToMe: false,
    dueDate: false,
    hasAssignee: false,
  });
  const [filteredJobs, setFilteredJobs] = React.useState<ActivityJobDto[]>([]);
  const { checkFeatureAllowed, user } = useAuth();

  React.useEffect(() => {
    setFilteredJobs(getFilteredJobs(jobs));
  }, [filters, jobs]);

  const getFilteredJobs = (items: ActivityJobDto[]) => {
    const results = items.filter((job) => {
      const filtersApplied =
        filters.constraint ||
        filters.ctc ||
        filters.critical ||
        filters.cost ||
        filters.unresolved ||
        filters.assignedToMe ||
        filters.dueDate ||
        filters.hasAssignee;
      if (!filtersApplied) return true;
      let shouldReturn = false;
      const hasConstraint = !!job.posts.find(
        (p: PostDto) => p.constraint && !p.resolved,
      );

      const hasCTC = !!job.posts.find((p: PostDto) => p.ctc && !p.resolved);
      const hasCritical = !!job.posts.find(
        (p: PostDto) => p.critical && !p.resolved,
      );
      const hasCost = !!job.posts.find((p: PostDto) => p.cost && !p.resolved);

      const hasOpen = !!job.posts.find((p: PostDto) => !p.resolved);

      const hasAssignedToMe = !!job.posts.find(
        (p: PostDto) => p.assignedToId === user?.id && !p.resolved,
      );

      const hasPerson = !!job.posts.find(
        (p: PostDto) => p.assignedToId !== null && !p.resolved,
      );

      const hasDate = !!job.posts.find((p: PostDto) => p.date !== null);
      if (filters.constraint && hasConstraint) shouldReturn = true;
      if (filters.ctc && hasCTC) shouldReturn = true;
      if (filters.critical && hasCritical) shouldReturn = true;
      if (filters.cost && hasCost) shouldReturn = true;
      if (filters.unresolved && hasOpen) shouldReturn = true;
      if (filters.assignedToMe && hasAssignedToMe) shouldReturn = true;
      if (filters.dueDate && hasDate) shouldReturn = true;
      if (filters.hasAssignee && hasPerson) shouldReturn = true;
      return shouldReturn;
    });
    return results;
  };
  const hasCTCAccess = checkFeatureAllowed(['ctc']);

  React.useEffect(() => {
    if (selectedJob) {
      const selected = jobs.find((j) => j.id === selectedJob.id);
      if (selected) {
        setJobName(`${selected.jobNumber} - ${selected.name}`);
      }
    }
  }, [selectedJob, jobs]);

  const toggleFilter = (
    field:
      | 'constraint'
      | 'ctc'
      | 'critical'
      | 'cost'
      | 'unresolved'
      | 'assignedToMe'
      | 'dueDate'
      | 'hasAssignee',
  ) => {
    setFilters((prevState) => {
      return {
        ...prevState,
        [field]: !prevState[field],
      };
    });
  };

  return (
    <>
      <Group gap="xs" mb="md">
        <BasicActionIcon
          variant={filters.constraint ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('constraint')}
          title="Constraint"
        >
          <IconBarrierBlock size={20} />
        </BasicActionIcon>
        <BasicActionIcon
          variant={filters.ctc ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('ctc')}
          title="CTC"
        >
          <IconCurrencyDollar size={20} />
        </BasicActionIcon>
        <BasicActionIcon
          variant={filters.critical ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('critical')}
          title="Critical"
        >
          <IconExclamationCircle size={20} />
        </BasicActionIcon>
        <BasicActionIcon
          variant={filters.cost ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('cost')}
          title="Cost"
        >
          <IconClockPlus size={20} />
        </BasicActionIcon>
        <BasicActionIcon
          variant={filters.dueDate ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('dueDate')}
          title="Has due date"
        >
          <IconCalendarMonth size={20} />
        </BasicActionIcon>
        <BasicActionIcon
          variant={filters.hasAssignee ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('hasAssignee')}
          title="Has assignee"
        >
          <IconUserCircle size={20} />
        </BasicActionIcon>

        <BasicButton
          variant={filters.unresolved ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('unresolved')}
          size="xs"
        >
          Open
        </BasicButton>
        <BasicButton
          variant={filters.assignedToMe ? 'filled' : 'light'}
          color="blue"
          onClick={() => toggleFilter('assignedToMe')}
          size="xs"
        >
          Assigned to Me
        </BasicButton>
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <JobTable
            jobs={filteredJobs}
            loading={loading}
            onSelectedJobChange={(details) => setSelectedJob(details)}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="md">
            {selectedJob ? (
              <ActivityFeed
                disablePadding
                jobId={selectedJob.id}
                jobNumber={selectedJob.jobNumber}
                stickyHeader={
                  <Group justify="space-between" mb="md">
                    <Title order={3}>
                      <AnchorLink
                        size="lg"
                        fw="800"
                        to={`/jobs/${selectedJob.jobNumber}/summary`}
                      >
                        {jobName}
                      </AnchorLink>
                    </Title>

                    {hasCTCAccess && (
                      <LinkButton
                        to={`/ctc/view/${selectedJob.jobNumber}`}
                        variant="light"
                        leftSection={<IconCurrencyDollar size={20} />}
                      >
                        View CTC
                      </LinkButton>
                    )}
                  </Group>
                }
              />
            ) : (
              <Text ta="center">Select a job in the left panel</Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </>
  );
};
